import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Calendar, ChevronDown, ChevronRight, FileText, Edit, X, Trash2, Users, Eye, EyeOff, Copy, Archive, ArchiveRestore, BookMarked, GripVertical } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import RichTextEditor from '../components/ui/rich-text-editor';
import StandardsMultiSelect from '../components/ui/standards-multi-select';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable checklist item component
const SortableChecklistItem = ({ item, index, allItems, section, placeholder, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 bg-white rounded border"
    >
      <button
        type="button"
        className="mt-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <input
        type="checkbox"
        checked={item.completed || false}
        onChange={(e) => {
          const nc = [...allItems];
          nc[index] = { ...nc[index], completed: e.target.checked };
          onUpdate(nc);
        }}
        className="mt-1 h-4 w-4"
      />
      <div className="flex-1">
        <Input
          value={item.text || ''}
          onChange={(e) => {
            const nc = [...allItems];
            nc[index] = { ...nc[index], text: e.target.value };
            onUpdate(nc);
          }}
          placeholder={placeholder}
          className="text-sm"
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          const nc = allItems.filter((_, idx) => idx !== index);
          onDelete(nc);
        }}
        className="text-red-600"
      >
        ✕
      </Button>
    </div>
  );
};

const LessonPlansPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [standards, setStandards] = useState([]);
  const [plans, setPlans] = useState([]);
  const [archivedPlans, setArchivedPlans] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [expandedAssignments, setExpandedAssignments] = useState({}); // Track which assignments are expanded
  const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicatingPlanId, setDuplicatingPlanId] = useState(null);
  const [duplicateSelectedChildren, setDuplicateSelectedChildren] = useState([]);
  const [availablePlansForMove, setAvailablePlansForMove] = useState([]);
  const [selectedPlanIdForMove, setSelectedPlanIdForMove] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lessonPlanAttachments, setLessonPlanAttachments] = useState([]);
  const [assignmentAttachments, setAssignmentAttachments] = useState([]);
  const { toast} = useToast();
  
  // Toggle assignment expansion
  const toggleAssignmentExpand = (assignmentId) => {
    setExpandedAssignments(prev => ({
      ...prev,
      [assignmentId]: !prev[assignmentId]
    }));
  };
  
  const [lessonFormData, setLessonFormData] = useState({
    subjectIds: [],  // Changed from subjectId to support multiple subjects
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    visibleToChildren: true,
    autoShowDayBefore: false,
    visibilityRule: 'always', // 'always', 'on_start_date', 'after_assignment'
    visibilityAfterAssignmentId: null,
    semesterId: '',
    standardIds: []
  });

  const [assignmentRows, setAssignmentRows] = useState([
    {
      id: Date.now(),
      title: '',
      description: '',
      dueDate: '',
      assignmentType: '',
      pointsPossible: '',
      checklist: [],
      standardIds: [],
      visibilityRule: 'always', // 'always', 'on_date', 'after_previous'
      visibilityDate: null,
      visibilityAfterAssignmentId: null
    }
  ]);
  const [assignmentStudents, setAssignmentStudents] = useState([]);
  const [assignmentSubject, setAssignmentSubject] = useState('');
  const [cascadeDates, setCascadeDates] = useState(false);
  const [cascadeConfirmOpen, setCascadeConfirmOpen] = useState(false);
  const [cascadePreview, setCascadePreview] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Load assignment types from localStorage
    const savedTypes = localStorage.getItem('assignmentTypes');
    if (savedTypes) {
      setAssignmentTypes(JSON.parse(savedTypes));
    } else {
      // Default types if not configured
      setAssignmentTypes([
        { id: '1', name: 'Test', weight: 40 },
        { id: '2', name: 'Quiz', weight: 20 },
        { id: '3', name: 'Homework', weight: 20 },
        { id: '4', name: 'Classwork', weight: 20 }
      ]);
    }
  }, []);

  useEffect(() => {
    // Auto-expand lesson plan if specified in URL
    const planId = searchParams.get('plan');
    if (planId) {
      setExpandedPlan(planId);
      
      // Scroll to the plan after a short delay to ensure it's rendered
      setTimeout(() => {
        const element = document.getElementById(`lesson-plan-${planId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [childrenData, plansData, archivedData, subjectsData, userProfile] = await Promise.all([
        api.getChildren(),
        api.getLessonPlans(),
        api.getLessonPlans(null, true),
        api.getSubjects(),
        api.getCurrentUser()
      ]);
      setChildren(childrenData);
      setPlans(plansData);
      setArchivedPlans(archivedData);
      // Load academic standards for the parent's state (non-blocking)
      try {
        const standardsData = await api.getStandards();
        setStandards(Array.isArray(standardsData) ? standardsData : []);
      } catch (e) {
        setStandards([]);
      }
      // Sort subjects alphabetically by name
      const sortedSubjects = subjectsData.sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setSubjects(sortedSubjects);
      
      // Load semesters from user profile
      if (userProfile && userProfile.semesters) {
        setSemesters(userProfile.semesters);
      }
      
      // Set first child as selected by default
      if (childrenData.length > 0 && !selectedChild) {
        setSelectedChild(childrenData[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load lesson plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child ? child.name : 'Unknown';
  };

  const getSubject = (subjectId) => {
    return subjects.find(s => s.id === subjectId);
  };

  const getAssignmentTypeName = (typeId) => {
    const type = assignmentTypes.find(t => t.id === typeId);
    return type ? type.name : 'Unknown';
  };

  const renderStandardBadges = (ids) => {
    if (!ids || ids.length === 0) return null;
    const matched = standards.filter(s => ids.includes(s.id));
    if (matched.length === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-1 mt-1.5">
        <BookMarked className="w-3 h-3 text-indigo-500" />
        {matched.map(s => (
          <Badge
            key={s.id}
            className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs"
            title={s.description}
          >
            {s.code}
          </Badge>
        ))}
      </div>
    );
  };

  // Helper to render text with clickable links
  const renderTextWithLinks = (text, className = '') => {
    if (!text) return null;
    if (!text.includes('http')) {
      return <span className={className}>{text}</span>;
    }
    // Split by spaces and make URLs clickable
    const words = text.split(' ');
    return (
      <span className={className}>
        {words.map((word, i) => {
          if (word.startsWith('http://') || word.startsWith('https://')) {
            return (
              <a
                key={i}
                href={word}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {word}{' '}
              </a>
            );
          }
          return <span key={i}>{word} </span>;
        })}
      </span>
    );
  };

  const toggleChild = (childId) => {
    if (selectedChildren.includes(childId)) {
      setSelectedChildren(selectedChildren.filter(id => id !== childId));
    } else {
      setSelectedChildren([...selectedChildren, childId]);
    }
  };

  const handleCreateLesson = async () => {
    console.log('handleCreateLesson called');
    console.log('isEditingLesson:', isEditingLesson);
    console.log('editingLessonId:', editingLessonId);
    console.log('selectedChildren:', selectedChildren);
    console.log('lessonFormData:', lessonFormData);
    
    try {
      if (isEditingLesson && editingLessonId) {
        console.log('EDITING MODE');
        // Editing an existing lesson plan
        const currentPlan = plans.find(p => p.id === editingLessonId);
        console.log('currentPlan:', currentPlan);
        
        // Find all plans that match this shared lesson plan
        const matchingPlans = plans.filter(p => 
          p.title === currentPlan.title && 
          p.subject_id === currentPlan.subject_id && 
          p.start_date === currentPlan.start_date &&
          p.end_date === currentPlan.end_date
        );
        
        console.log('matchingPlans:', matchingPlans.length);
        
        const currentStudentIds = matchingPlans.map(p => p.child_id);
        
        // Determine which students to add and remove
        const studentsToAdd = selectedChildren.filter(id => !currentStudentIds.includes(id));
        const studentsToRemove = currentStudentIds.filter(id => !selectedChildren.includes(id));
        
        console.log('studentsToAdd:', studentsToAdd);
        console.log('studentsToRemove:', studentsToRemove);
        
        // Update existing lesson plans
        for (const plan of matchingPlans) {
          if (selectedChildren.includes(plan.child_id)) {
            // Update this plan
            console.log('Updating plan:', plan.id);
            await api.updateLessonPlan(plan.id, {
              subject_ids: lessonFormData.subjectIds || [],
              title: lessonFormData.title,
              description: lessonFormData.description,
              start_date: new Date(lessonFormData.startDate).toISOString(),
              end_date: new Date(lessonFormData.endDate).toISOString(),
              visible_to_children: lessonFormData.visibleToChildren,
              auto_show_day_before: lessonFormData.autoShowDayBefore,
              visibility_rule: lessonFormData.visibilityRule || 'always',
              visibility_after_assignment_id: lessonFormData.visibilityAfterAssignmentId || null,
              semester_id: lessonFormData.semesterId || null,
              standard_ids: lessonFormData.standardIds || []
            });
          } else {
            // Remove this plan (student was deselected)
            console.log('Deleting plan:', plan.id);
            await api.deleteLessonPlan(plan.id);
          }
        }
        
        // Create new lesson plans for added students
        if (studentsToAdd.length > 0) {
          console.log('Creating new plans for added students');
          await api.createLessonPlan({
            child_ids: studentsToAdd,
            subject_ids: lessonFormData.subjectIds || [],
            title: lessonFormData.title,
            description: lessonFormData.description,
            start_date: new Date(lessonFormData.startDate).toISOString(),
            end_date: new Date(lessonFormData.endDate).toISOString(),
            visible_to_children: lessonFormData.visibleToChildren,
            auto_show_day_before: lessonFormData.autoShowDayBefore,
            visibility_rule: lessonFormData.visibilityRule || 'always',
            visibility_after_assignment_id: lessonFormData.visibilityAfterAssignmentId || null,
            semester_id: lessonFormData.semesterId || null,
            standard_ids: lessonFormData.standardIds || []
          });
        }
        
        toast({
          title: 'Lesson Plan Updated!',
          description: `Updated for ${selectedChildren.length} student(s).`
        });
      } else {
        console.log('CREATE MODE');
        // Create new lessons
        console.log('Creating new lesson plans');
        await api.createLessonPlan({
          child_ids: selectedChildren,
          subject_ids: lessonFormData.subjectIds || [],
          title: lessonFormData.title,
          description: lessonFormData.description,
          start_date: new Date(lessonFormData.startDate).toISOString(),
          end_date: new Date(lessonFormData.endDate).toISOString(),
          visible_to_children: lessonFormData.visibleToChildren,
          auto_show_day_before: lessonFormData.autoShowDayBefore,
          visibility_rule: lessonFormData.visibilityRule || 'always',
          visibility_after_assignment_id: lessonFormData.visibilityAfterAssignmentId || null,
          semester_id: lessonFormData.semesterId || null,
          standard_ids: lessonFormData.standardIds || []
        });
        
        toast({
          title: 'Lesson Plans Created!',
          description: `Created lesson plan(s) for ${selectedChildren.length} student(s).`
        });
      }

      console.log('Reloading data...');
      await loadData();
      console.log('Resetting form...');
      resetLessonForm();
      console.log('Done!');
    } catch (error) {
      console.error('Error in handleCreateLesson:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save lesson plan',
        variant: 'destructive'
      });
    }
  };

  const handleEditLesson = (plan) => {
    // Build subject_ids from both old and new format
    let subjectIds = plan.subject_ids || [];
    if (!subjectIds.length && plan.subject_id) {
      subjectIds = [plan.subject_id];
    }
    
    setLessonFormData({
      subjectIds: subjectIds,
      title: plan.title,
      description: plan.description,
      startDate: plan.start_date ? plan.start_date.split('T')[0] : '',
      endDate: plan.end_date ? plan.end_date.split('T')[0] : '',
      visibleToChildren: plan.visible_to_children !== undefined ? plan.visible_to_children : true,
      autoShowDayBefore: plan.auto_show_day_before || false,
      visibilityRule: plan.visibility_rule || 'always',
      visibilityAfterAssignmentId: plan.visibility_after_assignment_id || null,
      semesterId: plan.semester_id || '',
      standardIds: plan.standard_ids || []
    });
    setEditingLessonId(plan.id);
    setIsEditingLesson(true);
    
    // Find all students with this shared lesson plan
    const matchingPlans = plans.filter(p => 
      p.title === plan.title && 
      p.subject_id === plan.subject_id && 
      p.start_date === plan.start_date &&
      p.end_date === plan.end_date
    );
    
    // Set selected children to all students who have this lesson plan
    const studentIds = matchingPlans.map(p => p.child_id);
    setSelectedChildren(studentIds);
    
    setIsLessonDialogOpen(true);
  };

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this lesson plan? This will not delete the assignments.')) {
      try {
        await api.deleteLessonPlan(planId);
        toast({
          title: 'Lesson Plan Deleted',
          description: 'The lesson plan has been removed successfully.'
        });
        await loadData();
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete lesson plan',
          variant: 'destructive'
        });
      }
    }
  };

  const handleDuplicatePlan = async () => {
    if (duplicateSelectedChildren.length === 0) {
      toast({
        title: 'No Children Selected',
        description: 'Please select at least one child to duplicate the lesson plan to.',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.duplicateLessonPlan(duplicatingPlanId, duplicateSelectedChildren);
      toast({
        title: 'Lesson Plan Duplicated!',
        description: `Duplicated lesson plan to ${duplicateSelectedChildren.length} child(ren).`
      });
      setIsDuplicateDialogOpen(false);
      setDuplicatingPlanId(null);
      setDuplicateSelectedChildren([]);
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to duplicate lesson plan',
        variant: 'destructive'
      });
    }
  };

  const handleLessonPlanFileUpload = async (event, planId = null) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      // Upload file to S3
      const uploadResult = await api.uploadFile(file);
      
      const attachment = {
        name: file.name,
        url: uploadResult.url,
        content_type: uploadResult.content_type
      };

      if (planId) {
        // Adding to existing lesson plan
        await api.addLessonPlanAttachment(planId, attachment);
        toast({
          title: 'File Uploaded',
          description: `${file.name} has been added to the lesson plan.`
        });
        await loadData();
      } else {
        // Adding to new lesson plan being created
        setLessonPlanAttachments([...lessonPlanAttachments, attachment]);
        toast({
          title: 'File Ready',
          description: `${file.name} will be added when you save the lesson plan.`
        });
      }
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploadingFile(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleRemoveLessonPlanAttachment = async (planId, index) => {
    try {
      await api.deleteLessonPlanAttachment(planId, index);
      toast({
        title: 'Attachment Removed',
        description: 'The file has been removed from the lesson plan.'
      });
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove attachment',
        variant: 'destructive'
      });
    }
  };

  const handleAssignmentFileUpload = async (e, assignmentId = null) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      // Upload file to S3
      const uploadedFile = await api.uploadFile(file);
      
      const attachment = {
        name: file.name,
        url: uploadedFile.url
      };

      // ALWAYS just add to local state, save to DB when clicking "Update Assignment"
      setAssignmentAttachments([...assignmentAttachments, attachment]);
      
      toast({
        title: 'File Added',
        description: `${file.name} has been added. Click "Update Assignment" to save.`
      });
      
      // Clear the file input
      e.target.value = '';
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive'
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveAssignmentAttachment = async (assignmentId, index) => {
    try {
      await api.deleteAssignmentAttachment(assignmentId, index);
      
      // Update local state to remove the attachment immediately
      setAssignmentAttachments(assignmentAttachments.filter((_, i) => i !== index));
      
      toast({
        title: 'Attachment Removed',
        description: 'The file has been removed from the assignment.'
      });
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove attachment',
        variant: 'destructive'
      });
    }
  };

  const handleToggleVisibility = async (plan, newVisibility) => {
    try {
      // Update visibility for all lesson plans in the group (shared or individual)
      const plansToUpdate = plan.isShared 
        ? plan.allPlanIds || [plan.id]
        : [plan.id];
      
      for (const planId of plansToUpdate) {
        await api.updateLessonPlan(planId, {
          visible_to_children: newVisibility
        });
      }
      
      toast({
        title: newVisibility ? 'Lesson Plan Visible' : 'Lesson Plan Hidden',
        description: newVisibility 
          ? 'Students can now see this lesson plan and its assignments'
          : 'This lesson plan is now hidden from students'
      });
      
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update visibility',
        variant: 'destructive'
      });
    }
  };

  const handleArchivePlan = async (plan, archived) => {
    try {
      // Apply to all shared copies at once (or just this plan if individual)
      const planIds = plan.isShared ? (plan.allPlanIds || [plan.id]) : [plan.id];
      for (const planId of planIds) {
        await api.updateLessonPlan(planId, { archived });
      }
      toast({
        title: archived ? 'Lesson Plan Archived' : 'Lesson Plan Restored',
        description: archived
          ? 'Moved to Archived. It is now hidden from your view and from students.'
          : 'Restored to your active lesson plans.'
      });
      if (expandedPlan === plan.id) setExpandedPlan(null);
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update archive status',
        variant: 'destructive'
      });
    }
  };

  const handleCopyAssignment = async (plan, assignment) => {
    try {
      // Copy into the same lesson plan(s) the assignment belongs to
      const targetPlanIds = plan.isShared ? (plan.allPlanIds || [plan.id]) : [plan.id];
      for (const pid of targetPlanIds) {
        const targetPlan = plans.find(p => p.id === pid) || plan;
        await api.createAssignment({
          child_ids: [targetPlan.child_id],
          subject_id: targetPlan.subject_id,
          lesson_plan_id: pid,
          title: `Copy of ${assignment.title}`,
          description: assignment.description || '',
          date_assigned: assignment.date_assigned || assignment.dateAssigned,
          due_date: assignment.due_date || assignment.dueDate,
          assignment_type: assignment.assignment_type || assignment.assignmentType,
          points_possible: assignment.points_possible ?? assignment.pointsPossible ?? 0
        });
      }
      toast({
        title: 'Assignment Copied',
        description: 'A copy of the assignment was created in this lesson plan.'
      });
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to copy assignment',
        variant: 'destructive'
      });
    }
  };


  const handleAddAssignment = async () => {
    if (!expandedPlan) {
      console.log('No expanded plan');
      return;
    }

    // Find the expanded plan
    const currentPlan = plans.find(p => p.id === expandedPlan);
    if (!currentPlan) {
      console.log('Current plan not found');
      return;
    }

    // Validate all rows
    const invalidRows = assignmentRows.filter(row => 
      !row.title || !row.assignmentType || !row.pointsPossible || !row.dueDate
    );
    
    if (invalidRows.length > 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields (title, type, points, due date) for each assignment.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Determine which students to assign to
      let plansToAssign = [];
      
      if (assignmentStudents.length > 0) {
        // Students were manually selected - find their matching lesson plans
        // Look for plans that match the current plan's details
        const matchingPlans = plans.filter(p => 
          p.title === currentPlan.title && 
          p.subject_id === currentPlan.subject_id && 
          p.start_date === currentPlan.start_date &&
          p.end_date === currentPlan.end_date
        );
        
        console.log('Matching plans found:', matchingPlans.length);
        console.log('Selected students:', assignmentStudents);
        
        // Only include plans for selected students
        plansToAssign = matchingPlans.filter(p => assignmentStudents.includes(p.child_id));
        console.log('Plans to assign:', plansToAssign.length);
      } else {
        // No students selected - use only the current plan
        plansToAssign = [currentPlan];
        console.log('Using current plan only');
      }
      
      if (plansToAssign.length === 0) {
        toast({
          title: 'No Students Found',
          description: 'Could not find lesson plans for the selected students. Please try selecting students who have this lesson plan.',
          variant: 'destructive'
        });
        return;
      }
      
      // Create assignments for each row and each plan
      let totalCreated = 0;
      for (const row of assignmentRows) {
        for (const plan of plansToAssign) {
          const dueDateISO = row.dueDate + 'T12:00:00';
          
          const assignmentData = {
            child_ids: [plan.child_id],
            subject_id: assignmentSubject || plan.subject_id,
            lesson_plan_id: plan.id,  // Use the specific plan's ID for this student
            title: row.title,
            description: row.description,
            due_date: new Date(dueDateISO).toISOString(),
            assignment_type: row.assignmentType,
            points_possible: parseInt(row.pointsPossible),
            standard_ids: row.standardIds || [],
            checklist: row.checklist || []
          };

          await api.createAssignment(assignmentData);
          totalCreated++;
        }
      }

      toast({
        title: 'Assignments Created!',
        description: `Created ${totalCreated} assignment(s).`
      });

      // Reload data to show new assignments
      await loadData();
      resetAssignmentForm();
    } catch (error) {
      console.error('Failed to create assignments:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create assignments',
        variant: 'destructive'
      });
    }
  };

  const handleEditAssignment = (planId, assignment) => {
    setExpandedPlan(planId);
    setEditingAssignmentId(assignment.id);
    
    // For editing, show single row with existing data
    setAssignmentRows([{
      id: Date.now(),
      title: assignment.title,
      description: assignment.description,
      dueDate: assignment.due_date ? assignment.due_date.split('T')[0] : '',
      assignmentType: assignment.assignment_type || assignment.assignmentType || '',
      pointsPossible: assignment.points_possible || assignment.pointsPossible || '',
      checklist: assignment.checklist || [],
      standardIds: assignment.standard_ids || [],
      visibilityRule: assignment.visibility_rule || 'always',
      visibilityDate: assignment.visibility_date || null,
      visibilityAfterAssignmentId: assignment.visibility_after_assignment_id || null
    }]);
    
    // Load existing attachments
    setAssignmentAttachments(assignment.attachments || []);
    
    // Find the current plan and get available plans for the same subject
    const currentPlan = plans.find(p => p.id === planId);
    if (currentPlan) {
      // Get all unique lesson plans with the same subject
      const sameSubjectPlans = plans
        .filter(p => p.subject_id === currentPlan.subject_id)
        .reduce((acc, plan) => {
          // Group by unique lesson plan (title + dates)
          const key = `${plan.title}-${plan.start_date}-${plan.end_date}`;
          if (!acc.find(p => `${p.title}-${p.start_date}-${p.end_date}` === key)) {
            acc.push(plan);
          }
          return acc;
        }, []);
      
      setAvailablePlansForMove(sameSubjectPlans);
      setSelectedPlanIdForMove(planId); // Default to current plan
    }
    
    setAssignmentStudents([]);
    setIsAssignmentDialogOpen(true);
    setAssignmentSubject('');
  };

  const handleUpdateAssignment = async () => {
    if (!editingAssignmentId || assignmentRows.length === 0) return;

    const row = assignmentRows[0]; // Only one row when editing
    
    if (!row.title || !row.assignmentType || !row.pointsPossible || !row.dueDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    // If cascade is enabled and date changed, show confirmation first
    if (cascadeDates && editingAssignmentId) {
      // Find current assignment to check if date changed
      let currentAssignment = null;
      for (const plan of plans) {
        if (plan.assignments) {
          const found = plan.assignments.find(a => a.id === editingAssignmentId);
          if (found) {
            currentAssignment = found;
            break;
          }
        }
      }

      if (currentAssignment) {
        const oldDate = new Date(currentAssignment.due_date).toISOString().split('T')[0];
        const newDate = row.dueDate;
        
        if (oldDate !== newDate) {
          // Show cascade confirmation
          await handleCascadePreview();
          return;
        }
      }
    }

    // Proceed with normal update
    await performUpdate();
  };

  const handleCascadePreview = async () => {
    const row = assignmentRows[0];
    const dueDateISO = row.dueDate + 'T12:00:00';
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('access_token');
      
      // Call cascade endpoint to get preview
      const response = await fetch(
        `${backendUrl}/api/assignments/${editingAssignmentId}/cascade-dates?new_due_date=${new Date(dueDateISO).toISOString()}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to preview cascade');

      const result = await response.json();
      setCascadePreview(result.updates || []);
      setCascadeConfirmOpen(true);
    } catch (error) {
      console.error('Cascade preview failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to preview date changes',
        variant: 'destructive'
      });
    }
  };

  const confirmCascade = async () => {
    setCascadeConfirmOpen(false);
    await loadData();
    resetAssignmentForm();
    
    toast({
      title: 'Success!',
      description: `Updated ${cascadePreview.length} assignment(s) with cascading dates`
    });
  };

  const performUpdate = async () => {
    if (!editingAssignmentId || assignmentRows.length === 0) return;

    const row = assignmentRows[0]; // Only one row when editing
    
    console.log('Starting update for assignment:', editingAssignmentId);
    console.log('Row data:', row);
    console.log('Selected students to add:', assignmentStudents);
    
    if (!row.title || !row.assignmentType || !row.pointsPossible || !row.dueDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const dueDateISO = row.dueDate + 'T12:00:00';
      
      const updateData = {
        title: row.title,
        description: row.description,
        due_date: new Date(dueDateISO).toISOString(),
        assignment_type: row.assignmentType,
        points_possible: parseInt(row.pointsPossible),
        attachments: assignmentAttachments,
        checklist: row.checklist || [],
        standard_ids: row.standardIds || [],
        visibility_rule: row.visibilityRule || 'always',
        visibility_date: row.visibilityDate || null,
        visibility_after_assignment_id: row.visibilityAfterAssignmentId || null
      };
      
      console.log('Update data:', updateData);
      console.log('🔍 ATTACHMENTS IN UPDATE:', assignmentAttachments);
      console.log('🔍 ATTACHMENTS LENGTH:', assignmentAttachments.length);

      // Find the current assignment being edited
      let currentAssignment = null;
      let currentPlan = null;
      for (const plan of plans) {
        if (plan.assignments) {
          const found = plan.assignments.find(a => a.id === editingAssignmentId);
          if (found) {
            currentAssignment = found;
            currentPlan = plan;
            break;
          }
        }
      }

      console.log('Current assignment found:', currentAssignment);

      if (currentAssignment && currentPlan) {
        // Find all matching assignments across all students
        // Matching criteria: same lesson plan details (shared assignments)
        // NOTE: We do NOT match on due_date because we're trying to UPDATE it!
        const matchingAssignments = [];
        const existingChildIds = new Set();
        
        for (const plan of plans) {
          if (plan.assignments) {
            for (const assignment of plan.assignments) {
              const assignmentPlan = plans.find(p => p.id === assignment.lesson_plan_id);
              
              if (currentPlan && assignmentPlan &&
                  currentPlan.title === assignmentPlan.title &&
                  currentPlan.subject_id === assignmentPlan.subject_id &&
                  currentPlan.start_date === assignmentPlan.start_date &&
                  currentPlan.end_date === assignmentPlan.end_date &&
                  assignment.title === currentAssignment.title) {
                // This is a matching shared assignment
                matchingAssignments.push(assignment.id);
                existingChildIds.add(plan.child_id);
                console.log('✅ MATCH FOUND:', assignment.id, 'for child', plan.child_id);
              }
            }
          }
        }

        console.log('Found matching assignments:', matchingAssignments.length, matchingAssignments);
        console.log('Existing child IDs:', Array.from(existingChildIds));

        // CRITICAL: Always include the current assignment being edited
        if (!matchingAssignments.includes(editingAssignmentId)) {
          console.log('⚠️ Current assignment not in matches, adding it explicitly');
          matchingAssignments.push(editingAssignmentId);
          // Also add its child_id to existing children
          if (currentPlan.child_id) {
            existingChildIds.add(currentPlan.child_id);
          }
        }

        // Update all matching assignments
        let updatedCount = 0;
        for (const assignmentId of matchingAssignments) {
          console.log('Updating assignment ID:', assignmentId);
          await api.updateAssignment(assignmentId, updateData);
          updatedCount++;
        }
        
        // Create assignments for newly selected students
        let createdCount = 0;
        if (assignmentStudents.length > 0) {
          const newStudentIds = assignmentStudents.filter(childId => !existingChildIds.has(childId));
          console.log('New students to add:', newStudentIds);
          
          for (const childId of newStudentIds) {
            // Find the lesson plan for this child
            const childPlan = plans.find(p => 
              p.child_id === childId &&
              p.title === currentPlan.title &&
              p.subject_id === currentPlan.subject_id &&
              p.start_date === currentPlan.start_date &&
              p.end_date === currentPlan.end_date
            );
            
            if (childPlan) {
              console.log('Creating assignment for child', childId, 'in plan', childPlan.id);
              
              const newAssignmentData = {
                ...updateData,
                lesson_plan_id: childPlan.id,
                child_ids: [childId],  // Backend expects child_ids as an array
                subject_id: currentPlan.subject_id
              };
              
              await api.createAssignment(newAssignmentData);
              createdCount++;
            }
          }
        }
        
        console.log('Total updated:', updatedCount, 'Total created:', createdCount);

        toast({
          title: 'Success!',
          description: `Updated ${updatedCount} assignment${updatedCount > 1 ? 's' : ''}` +
            (createdCount > 0 ? ` and created ${createdCount} new assignment${createdCount > 1 ? 's' : ''}` : '') +
            '.'
        });
      } else {
        console.log('Current assignment not found in plans, using fallback');
        // Fallback - just update the single assignment
        await api.updateAssignment(editingAssignmentId, updateData);

        toast({
          title: 'Assignment Updated!',
          description: 'Assignment updated successfully.'
        });
      }

      // Reload data to show updated assignments
      console.log('Reloading data...');
      await loadData();
      resetAssignmentForm();
      setIsAssignmentDialogOpen(false); // Close the dialog after successful update
    } catch (error) {
      console.error('Failed to update assignment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update assignment',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAssignment = async (planId, assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await api.deleteAssignment(assignmentId);
      
      toast({
        title: 'Assignment Deleted',
        description: 'Assignment has been permanently deleted.'
      });

      // Reload data to reflect deletion
      await loadData();
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete assignment',
        variant: 'destructive'
      });
    }
  };

  const resetLessonForm = () => {
    setLessonFormData({ subjectIds: [], title: '', description: '', startDate: '', endDate: '', visibleToChildren: true, autoShowDayBefore: false, visibilityRule: 'always', visibilityAfterAssignmentId: null, semesterId: '', standardIds: [] });
    setSelectedChildren([]);
    setIsEditingLesson(false);
    setEditingLessonId(null);
    setIsLessonDialogOpen(false);
  };

  const resetAssignmentForm = () => {
    setAssignmentRows([{
      id: Date.now(),
      title: '',
      description: '',
      dueDate: '',
      assignmentType: '',
      pointsPossible: '',
      checklist: [],
      standardIds: [],
      visibilityRule: 'always',
      visibilityDate: null,
      visibilityAfterAssignmentId: null
    }]);
    setAssignmentStudents([]);
    setAssignmentSubject('');
    setEditingAssignmentId(null);
  };

  const addAssignmentRow = () => {
    if (assignmentRows.length >= 31) {
      toast({
        title: 'Maximum Reached',
        description: 'You can add up to 31 assignments at once.',
        variant: 'destructive'
      });
      return;
    }
    
    setAssignmentRows([...assignmentRows, {
      id: Date.now(),
      title: '',
      description: '',
      dueDate: '',
      assignmentType: '',
      pointsPossible: ''
    }]);
  };

  const duplicateAssignmentRow = (id) => {
    if (assignmentRows.length >= 31) {
      toast({
        title: 'Maximum Reached',
        description: 'You can add up to 31 assignments at once.',
        variant: 'destructive'
      });
      return;
    }
    
    const rowToDuplicate = assignmentRows.find(row => row.id === id);
    if (rowToDuplicate) {
      const newRow = {
        id: Date.now(),
        title: rowToDuplicate.title,
        description: rowToDuplicate.description,
        dueDate: rowToDuplicate.dueDate,
        assignmentType: rowToDuplicate.assignmentType,
        pointsPossible: rowToDuplicate.pointsPossible
      };
      
      // Find the index of the row to duplicate and insert after it
      const index = assignmentRows.findIndex(row => row.id === id);
      const newRows = [...assignmentRows];
      newRows.splice(index + 1, 0, newRow);
      setAssignmentRows(newRows);
      
      toast({
        title: 'Assignment Duplicated',
        description: 'Row copied. Edit as needed.',
      });
    }
  };

  const removeAssignmentRow = (id) => {
    if (assignmentRows.length === 1) {
      toast({
        title: 'Cannot Remove',
        description: 'At least one assignment row is required.',
        variant: 'destructive'
      });
      return;
    }
    setAssignmentRows(assignmentRows.filter(row => row.id !== id));
  };

  const updateAssignmentRow = (id, field, value) => {
    setAssignmentRows(assignmentRows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const toggleAssignmentStudent = (childId) => {
    if (assignmentStudents.includes(childId)) {
      setAssignmentStudents(assignmentStudents.filter(id => id !== childId));
    } else {
      setAssignmentStudents([...assignmentStudents, childId]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading lesson plans...</div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Lesson Plans</h1>
          <Card className="p-12 text-center border-0 shadow-lg">
            <Users className="w-24 h-24 text-slate-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">No Students Added Yet</h2>
            <p className="text-slate-600 mb-6">Add students in Settings to create lesson plans for them.</p>
            <Button
              onClick={() => navigate('/settings')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Go to Settings
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Lesson Plans</h1>
            <p className="text-slate-600 text-lg">Create lesson plans and assignments for multiple students</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowArchived(v => !v)}
              className={`px-5 py-6 border-2 ${showArchived ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-5 h-5 mr-2" />
              {showArchived ? 'Hide Archived' : `Archived${archivedPlans.length ? ` (${archivedPlans.length})` : ''}`}
            </Button>
            <Button
              onClick={() => setIsLessonDialogOpen(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Lesson Plan
            </Button>
          </div>
        </div>

        {/* Student Tabs */}
        <div className="mb-8">
          <div className="flex gap-4 overflow-x-auto pb-2">
            {children.map(child => {
              const childPlans = plans.filter(p => p.child_id === child.id);
              const isSelected = selectedChild === child.id;
              
              return (
                <button
                  key={child.id}
                  onClick={() => setSelectedChild(child.id)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    isSelected
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg'
                      : 'bg-white text-slate-700 hover:shadow-md border border-slate-200'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold ${
                      isSelected ? 'bg-white text-purple-600' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {child.name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{child.name}</p>
                    <p className={`text-sm ${
                      isSelected ? 'text-purple-100' : 'text-slate-500'
                    }`}>
                      {childPlans.length} plan{childPlans.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Archived Lesson Plans */}
        {showArchived && (
          <div className="mb-8" data-testid="archived-section">
            <h3 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Archived Lesson Plans
            </h3>
            {(() => {
              const childArchived = archivedPlans.filter(p => p.child_id === selectedChild);
              if (childArchived.length === 0) {
                return (
                  <Card className="p-6 text-center border-0 shadow-sm bg-amber-50/50">
                    <p className="text-slate-500 text-sm">No archived lesson plans for this student.</p>
                  </Card>
                );
              }
              const seen = new Set();
              const list = [];
              childArchived.forEach(plan => {
                const sig = `${plan.title}|${plan.subject_id}|${plan.start_date}|${plan.end_date}`;
                if (seen.has(sig)) return;
                seen.add(sig);
                const matches = archivedPlans.filter(p =>
                  p.title === plan.title &&
                  p.subject_id === plan.subject_id &&
                  p.start_date === plan.start_date &&
                  p.end_date === plan.end_date
                );
                const uniqueChildIds = [...new Set(matches.map(m => m.child_id))];
                list.push({ ...plan, isShared: uniqueChildIds.length > 1, allPlanIds: matches.map(m => m.id), sharedCount: uniqueChildIds.length });
              });
              return (
                <div className="space-y-3">
                  {list.map(plan => {
                    const subject = getSubject(plan.subject_id);
                    return (
                      <Card key={plan.id} className="p-4 border-0 shadow-sm bg-amber-50/40" data-testid={`archived-plan-${plan.id}`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                              style={{ backgroundColor: `${subject?.color}20`, color: subject?.color }}
                            >
                              {subject?.icon}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-slate-700 truncate">{plan.title}</h4>
                                {plan.isShared && (
                                  <Badge className="bg-blue-100 text-blue-700 border-blue-300 flex items-center gap-1 text-xs">
                                    <Users className="w-3 h-3" />
                                    {plan.sharedCount} students
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                <span>{subject?.name}</span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                                </span>
                                <span>{plan.assignments?.length || 0} assignments</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchivePlan(plan, false)}
                              className="border-amber-300 text-amber-700 hover:bg-amber-100"
                              data-testid={`unarchive-plan-btn-${plan.id}`}
                            >
                              <ArchiveRestore className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeletePlan(plan.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`archived-delete-plan-btn-${plan.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}


        {/* Lesson Plans List */}
        <div className="space-y-4">
          {(() => {
            const childPlans = plans.filter(plan => plan.child_id === selectedChild);
            
            // Separate shared lesson plans (those with same title/subject/dates for multiple students)
            const sharedPlans = [];
            const individualPlans = [];
            
            childPlans.forEach(plan => {
              // Check if this plan is shared with other students
              const matchingPlans = plans.filter(p => 
                p.title === plan.title && 
                p.subject_id === plan.subject_id && 
                p.start_date === plan.start_date &&
                p.end_date === plan.end_date &&
                p.id !== plan.id
              );
              
              if (matchingPlans.length > 0 && !sharedPlans.find(sp => sp.id === plan.id)) {
                // Get all children with this shared plan
                const allMatchingPlans = plans.filter(p => 
                  p.title === plan.title && 
                  p.subject_id === plan.subject_id && 
                  p.start_date === plan.start_date &&
                  p.end_date === plan.end_date
                );
                
                // Only include plans for children that currently exist
                const validMatchingPlans = allMatchingPlans.filter(p => 
                  children.some(c => c.id === p.child_id)
                );
                
                // Get unique children (deduplicate by child_id)
                const uniqueChildIds = [...new Set(validMatchingPlans.map(p => p.child_id))];
                const sharedWithChildren = uniqueChildIds
                  .map(childId => children.find(c => c.id === childId))
                  .filter(Boolean);
                
                sharedPlans.push({
                  ...plan,
                  isShared: true,
                  sharedWith: sharedWithChildren,
                  allPlanIds: validMatchingPlans.map(p => p.id)
                });
              } else if (!matchingPlans.length) {
                individualPlans.push(plan);
              }
            });
            
            // Combine all plans and sort: visible first, hidden at the bottom
            const allPlansToShow = [...sharedPlans, ...individualPlans];
            
            allPlansToShow.sort((a, b) => {
              const aVisible = a.visible_to_children !== false;
              const bVisible = b.visible_to_children !== false;
              if (aVisible && !bVisible) return -1;
              if (!aVisible && bVisible) return 1;
              return 0;
            });
            
            if (allPlansToShow.length === 0) {
              const child = children.find(c => c.id === selectedChild);
              return (
                <Card className="p-12 text-center border-0 shadow-lg">
                  <FileText className="w-24 h-24 text-slate-300 mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">No Lesson Plans</h2>
                  <p className="text-slate-600">
                    No lesson plans found for {child?.name}. Click "Create Lesson Plan" to get started.
                  </p>
                </Card>
              );
            }
            
            return (
              <>
                {sharedPlans.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Shared Lesson Plans
                    </h3>
                  </div>
                )}
                
                {allPlansToShow.map(plan => {
            // Build subject_ids from both old and new format
            const planSubjectIds = plan.subject_ids?.length ? plan.subject_ids : (plan.subject_id ? [plan.subject_id] : []);
            const planSubjects = planSubjectIds.map(id => getSubject(id)).filter(Boolean);
            const primarySubject = planSubjects[0];
            const isExpanded = expandedPlan === plan.id;
            const isHidden = plan.visible_to_children === false;
            
            return (
              <Card 
                key={plan.id} 
                id={`lesson-plan-${plan.id}`} 
                className={`border-0 shadow-md overflow-hidden transition-all ${
                  isHidden 
                    ? 'bg-slate-50 opacity-60' 
                    : 'bg-white'
                }`}
              >
                {/* Lesson Plan Header - Collapsed/Accordion Style */}
                <div
                  className={`p-3 cursor-pointer transition-colors ${
                    isHidden 
                      ? 'hover:bg-slate-100' 
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                  data-testid={`lesson-plan-header-${plan.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button className="text-slate-400 hover:text-slate-600 shrink-0">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      {/* Subject icon(s) - smaller in collapsed view */}
                      <div className="flex -space-x-1 shrink-0">
                        {planSubjects.slice(0, 2).map((subj, idx) => (
                          <div
                            key={subj?.id || idx}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-lg border-2 border-white"
                            style={{ backgroundColor: `${subj?.color}20`, color: subj?.color, zIndex: 2 - idx }}
                            title={subj?.name}
                          >
                            {subj?.icon}
                          </div>
                        ))}
                        {planSubjects.length > 2 && (
                          <div className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold bg-slate-100 text-slate-600 border-2 border-white">
                            +{planSubjects.length - 2}
                          </div>
                        )}
                      </div>
                      <h3 className={`font-semibold truncate ${isHidden ? 'text-slate-500' : 'text-slate-900'}`}>
                        {plan.title}
                      </h3>
                      {plan.isShared && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs shrink-0">
                          <Users className="w-3 h-3 mr-1" />
                          {plan.sharedWith.length}
                        </Badge>
                      )}
                      {!plan.visible_to_children && (
                        <Badge className="bg-slate-100 text-slate-600 text-xs shrink-0">
                          <EyeOff className="w-3 h-3" />
                        </Badge>
                      )}
                      <Badge className="bg-purple-100 text-purple-700 text-xs shrink-0">
                        {plan.assignments?.length || 0} assignments
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-slate-500 hidden sm:inline">
                        {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                      </span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditLesson(plan);
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                          data-testid={`edit-plan-btn-${plan.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDuplicatingPlanId(plan.id);
                            setDuplicateSelectedChildren([]);
                            setIsDuplicateDialogOpen(true);
                          }}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchivePlan(plan, true);
                          }}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 w-7 p-0"
                          title="Archive"
                          data-testid={`archive-plan-btn-${plan.id}`}
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePlan(plan.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                          data-testid={`delete-plan-btn-${plan.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {/* Description & Meta Info */}
                    <div className="px-4 py-3 bg-white border-b border-slate-100">
                      {plan.description && (
                        <div
                          className={`text-sm mb-3 prose prose-sm max-w-none ${isHidden ? 'text-slate-400' : 'text-slate-600'}`}
                          dangerouslySetInnerHTML={{ __html: plan.description }}
                        />
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>
                          {plan.isShared ? (
                            <>
                              {plan.sharedWith.map((child, idx) => (
                                <span key={child.id}>
                                  {child.name}{idx < plan.sharedWith.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </>
                          ) : (
                            getChildName(plan.child_id)
                          )}
                        </span>
                        <span>•</span>
                        <span>{planSubjects.map(s => s?.name).join(', ')}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                        </span>
                        {plan.semester_id && (() => {
                          const semester = semesters.find(s => s.id === plan.semester_id);
                          return semester ? (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              {semester.name}
                            </Badge>
                          ) : null;
                        })()}
                        <div 
                          className="flex items-center gap-2 ml-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className={`text-xs font-medium ${isHidden ? 'text-slate-400' : 'text-slate-600'}`}>
                            {isHidden ? 'Hidden' : 'Visible'}
                          </span>
                          <Switch
                            checked={!isHidden}
                            onCheckedChange={(checked) => handleToggleVisibility(plan, checked)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>
                      </div>
                      {renderStandardBadges(plan.standard_ids)}
                    </div>

                    {/* Assignments Section */}
                    <div className="bg-slate-50 p-4">
                    {/* Attachments Section */}
                    {plan.attachments && plan.attachments.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-bold text-slate-900 mb-2 text-sm">📎 Attachments</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {plan.attachments.map((att, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border hover:border-purple-300">
                              <span className="text-sm truncate flex-1">{att.name}</span>
                              <div className="flex gap-1 ml-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(att.url, '_blank');
                                  }}
                                  className="text-blue-600 h-6 w-6 p-0"
                                  title="Download"
                                >
                                  📥
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const printWindow = window.open(att.url, '_blank');
                                    if (printWindow) {
                                      printWindow.onload = () => printWindow.print();
                                    }
                                  }}
                                  className="text-green-600"
                                  title="Print"
                                >
                                  🖨️
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveLessonPlanAttachment(plan.id, idx);
                                  }}
                                  className="text-red-600"
                                  title="Remove"
                                >
                                  ✕
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-slate-900">Assignments</h4>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedPlan(plan.id);
                          setIsAssignmentDialogOpen(true);
                        }}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Assignment
                      </Button>
                    </div>

                    {plan.assignments && plan.assignments.length > 0 ? (
                      <div className="space-y-3">
                        {plan.assignments
                          .sort((a, b) => {
                            // First, sort by completion status (incomplete first, completed last)
                            const completedStatuses = ['graded', 'completed'];
                            const aCompleted = completedStatuses.includes(a.status);
                            const bCompleted = completedStatuses.includes(b.status);
                            
                            if (aCompleted && !bCompleted) return 1;  // a is completed, move to bottom
                            if (!aCompleted && bCompleted) return -1; // b is completed, a goes to top
                            
                            // If same completion status, sort by due date
                            const dateA = new Date(a.due_date || a.dueDate);
                            const dateB = new Date(b.due_date || b.dueDate);
                            return dateA - dateB;
                          })
                          .map(assignment => {
                          const isCompleted = ['graded', 'completed'].includes(assignment.status);
                          const isExpanded = expandedAssignments[assignment.id];
                          return (
                          <div
                            key={assignment.id}
                            className={`rounded-lg border transition-all ${
                              isCompleted 
                                ? 'bg-slate-50 border-slate-200 opacity-75' 
                                : 'bg-white border-slate-200 hover:shadow-md'
                            }`}
                          >
                            {/* Collapsed header - always visible */}
                            <div 
                              className="p-3 flex items-center justify-between gap-3 cursor-pointer"
                              onClick={() => toggleAssignmentExpand(assignment.id)}
                              data-testid={`assignment-header-${assignment.id}`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <button className="text-slate-400 hover:text-slate-600 shrink-0">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                                <FileText className={`w-4 h-4 shrink-0 ${isCompleted ? 'text-green-500' : 'text-slate-600'}`} />
                                <h5 className={`font-medium truncate ${isCompleted ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                  {assignment.title}
                                </h5>
                                <Badge className="bg-blue-100 text-blue-700 text-xs shrink-0">
                                  {getAssignmentTypeName(assignment.assignment_type || assignment.assignmentType)}
                                </Badge>
                                {isCompleted && (
                                  <Badge className="bg-green-100 text-green-700 text-xs shrink-0">✓</Badge>
                                )}
                                {assignment.status === 'submitted' && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs shrink-0">Review</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className={`text-xs ${isCompleted ? 'text-slate-400' : 'text-slate-500'}`}>
                                  Due: {new Date(assignment.due_date || assignment.dueDate).toLocaleDateString()}
                                </span>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditAssignment(plan.id, assignment);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                                    data-testid={`edit-assignment-btn-${assignment.id}`}
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyAssignment(plan, assignment);
                                    }}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"
                                    title="Copy assignment"
                                    data-testid={`copy-assignment-btn-${assignment.id}`}
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAssignment(plan.id, assignment.id);
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                    data-testid={`delete-assignment-btn-${assignment.id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded content */}
                            {isExpanded && (
                              <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                                <div className="pt-3">
                                  <div
                                    className={`text-sm mb-3 prose prose-sm max-w-none ${isCompleted ? 'text-slate-400' : 'text-slate-600'}`}
                                    dangerouslySetInnerHTML={{ __html: assignment.description || '<span class="text-slate-400 italic">No description</span>' }}
                                  />
                                  <div className={`flex items-center gap-4 text-xs ${isCompleted ? 'text-slate-400' : 'text-slate-500'}`}>
                                    <span>Assigned: {new Date(assignment.date_assigned || assignment.dateAssigned).toLocaleDateString()}</span>
                                    <span>Points: {assignment.points_possible || assignment.pointsPossible || 100}</span>
                                  </div>
                                  {renderStandardBadges(assignment.standard_ids)}
                                  {assignment.checklist && assignment.checklist.length > 0 && (
                                    <div className="mt-3 space-y-2" data-testid={`assignment-checklist-${assignment.id}`}>
                                      {[
                                        { key: 'progress', label: 'Progress Tracker' },
                                        { key: 'resources', label: 'Resources' },
                                      ].map(section => {
                                        const items = assignment.checklist.filter(i => (i.section || 'progress') === section.key);
                                        if (items.length === 0) return null;
                                        return (
                                          <div key={section.key} className="space-y-1">
                                            <p className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                                              <FileText className="w-3 h-3" /> {section.label} ({items.filter(i => i.completed).length}/{items.length})
                                            </p>
                                            {items.map((item, idx) => (
                                              <div key={item.id || idx} className="flex items-center gap-2 text-xs text-slate-600">
                                                <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${item.completed ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300'}`}>
                                                  {item.completed && <span className="text-[9px] leading-none">✓</span>}
                                                </span>
                                                {renderTextWithLinks(item.text, item.completed ? 'line-through text-slate-400' : '')}
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )})}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-slate-200">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                        <p className="text-slate-500">No assignments yet</p>
                        <p className="text-xs text-slate-400 mt-1">Click "Add Assignment" to get started</p>
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
              </>
            );
          })()}

          {plans.length === 0 && (
            <Card className="p-12 border-0 shadow-lg bg-white text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Lesson Plans Yet</h3>
              <p className="text-slate-600 mb-4">Create your first lesson plan to organize learning</p>
            </Card>
          )}
        </div>
      </div>

      {/* Create Lesson Plan Dialog */}
      <Dialog open={isLessonDialogOpen} onOpenChange={(open) => {
        setIsLessonDialogOpen(open);
        if (!open) resetLessonForm();
      }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {isEditingLesson ? 'Edit Lesson Plan' : 'Create Lesson Plan'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Students (select multiple)</Label>
              <div className="mt-2 border rounded-lg p-3 bg-slate-50 max-h-32 overflow-y-auto">
                <div className="space-y-2">
                  {children.map(child => (
                    <div key={child.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`child-${child.id}`}
                        checked={selectedChildren.includes(child.id)}
                        onCheckedChange={() => toggleChild(child.id)}
                      />
                      <Label htmlFor={`child-${child.id}`} className="cursor-pointer text-sm font-normal flex-1">
                        {child.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Subjects</Label>
              <p className="text-xs text-slate-500 mb-2">Select one or more subjects for this lesson plan</p>
              <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {subjects.map(subject => (
                  <div 
                    key={subject.id} 
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                      lessonFormData.subjectIds?.includes(subject.id) ? 'bg-blue-50 border border-blue-200' : ''
                    }`}
                    onClick={() => {
                      const currentIds = lessonFormData.subjectIds || [];
                      const newIds = currentIds.includes(subject.id)
                        ? currentIds.filter(id => id !== subject.id)
                        : [...currentIds, subject.id];
                      setLessonFormData({ ...lessonFormData, subjectIds: newIds });
                    }}
                  >
                    <Checkbox 
                      checked={lessonFormData.subjectIds?.includes(subject.id)} 
                      onCheckedChange={(checked) => {
                        const currentIds = lessonFormData.subjectIds || [];
                        const newIds = checked
                          ? [...currentIds, subject.id]
                          : currentIds.filter(id => id !== subject.id);
                        setLessonFormData({ ...lessonFormData, subjectIds: newIds });
                      }}
                    />
                    <span className="text-sm">{subject.icon} {subject.name}</span>
                  </div>
                ))}
              </div>
              {lessonFormData.subjectIds?.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{lessonFormData.subjectIds.length} subject(s) selected</p>
              )}
            </div>

            <div>
              <Label>Semester (Optional)</Label>
              <Select value={lessonFormData.semesterId || 'none'} onValueChange={(value) => setLessonFormData({ ...lessonFormData, semesterId: value === 'none' ? '' : value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select semester" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Semester</SelectItem>
                  {semesters.map(semester => (
                    <SelectItem key={semester.id} value={semester.id}>
                      {semester.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Lesson Plan Title</Label>
              <Input
                value={lessonFormData.title}
                onChange={(e) => setLessonFormData({ ...lessonFormData, title: e.target.value })}
                placeholder="e.g., Fractions Unit"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Description</Label>
              <div className="mt-2">
                <RichTextEditor
                  value={lessonFormData.description}
                  onChange={(value) => setLessonFormData({ ...lessonFormData, description: value })}
                  placeholder="What will be covered..."
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><BookMarked className="w-4 h-4" /> Standards Covered (Optional)</Label>
              <div className="mt-2">
                <StandardsMultiSelect
                  standards={standards}
                  value={lessonFormData.standardIds || []}
                  onChange={(ids) => setLessonFormData({ ...lessonFormData, standardIds: ids })}
                  testId="lesson-standards"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={lessonFormData.startDate}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, startDate: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={lessonFormData.endDate}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, endDate: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Visibility Options */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="text-sm font-semibold">Student Visibility</Label>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!lessonFormData.visibleToChildren}
                    onCheckedChange={(checked) => 
                      setLessonFormData({ 
                        ...lessonFormData, 
                        visibleToChildren: !checked,
                        visibilityRule: checked ? lessonFormData.visibilityRule : 'always'
                      })
                    }
                  />
                  <div>
                    <Label className="text-sm font-medium cursor-pointer">
                      {lessonFormData.visibleToChildren ? <Eye className="w-4 h-4 inline mr-1" /> : <EyeOff className="w-4 h-4 inline mr-1" />}
                      Hide from students
                    </Label>
                    <p className="text-xs text-slate-500">Students won't see this lesson plan or its assignments</p>
                  </div>
                </div>
              </div>

              {/* Visibility Rule - shown when NOT hidden */}
              {lessonFormData.visibleToChildren && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs text-slate-600">When should students see this?</Label>
                  <Select
                    value={lessonFormData.visibilityRule}
                    onValueChange={(value) => setLessonFormData({ 
                      ...lessonFormData, 
                      visibilityRule: value,
                      visibilityAfterAssignmentId: value === 'after_assignment' ? lessonFormData.visibilityAfterAssignmentId : null
                    })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="always">Always visible</SelectItem>
                      <SelectItem value="on_start_date">When start date arrives</SelectItem>
                      <SelectItem value="after_assignment">After completing an assignment</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {lessonFormData.visibilityRule === 'on_start_date' && lessonFormData.startDate && (
                    <p className="text-xs text-blue-600">
                      Will become visible on {new Date(lessonFormData.startDate).toLocaleDateString()}
                    </p>
                  )}
                  
                  {lessonFormData.visibilityRule === 'after_assignment' && (
                    <div className="mt-2">
                      <Label className="text-xs text-slate-600">Select assignment that must be completed first:</Label>
                      <Select
                        value={lessonFormData.visibilityAfterAssignmentId || ''}
                        onValueChange={(value) => setLessonFormData({ ...lessonFormData, visibilityAfterAssignmentId: value })}
                      >
                        <SelectTrigger className="bg-white mt-1">
                          <SelectValue placeholder="Select an assignment..." />
                        </SelectTrigger>
                        <SelectContent>
                          {plans
                            .flatMap(p => p.assignments || [])
                            .filter(a => a.id !== lessonFormData.id)
                            .slice(0, 50)
                            .map(a => (
                              <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Legacy auto-show option - only when hidden */}
              {!lessonFormData.visibleToChildren && lessonFormData.startDate && (
                <div className="ml-8 pl-4 border-l-2 border-slate-300">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="auto-show"
                      checked={lessonFormData.autoShowDayBefore}
                      onCheckedChange={(checked) => 
                        setLessonFormData({ ...lessonFormData, autoShowDayBefore: checked })
                      }
                    />
                    <div>
                      <label htmlFor="auto-show" className="text-sm font-medium cursor-pointer">
                        Automatically show 1 day before start date
                      </label>
                      <p className="text-xs text-slate-500 mt-1">
                        Lesson will become visible on {lessonFormData.startDate ? 
                          new Date(new Date(lessonFormData.startDate).getTime() - 86400000).toLocaleDateString() : 
                          '(select start date)'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold">Attachments (PDFs, worksheets, etc.)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  id="lesson-file-upload"
                  className="hidden"
                  onChange={(e) => handleLessonPlanFileUpload(e, editingLessonId)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('lesson-file-upload').click()}
                  disabled={uploadingFile}
                  className="w-full"
                >
                  {uploadingFile ? 'Uploading...' : '📎 Add File'}
                </Button>
              </div>
              
              {/* Display attachments */}
              {lessonPlanAttachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {lessonPlanAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                      <span className="text-sm truncate flex-1">{att.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setLessonPlanAttachments(lessonPlanAttachments.filter((_, i) => i !== idx));
                        }}
                        className="text-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={handleCreateLesson}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6"
              disabled={selectedChildren.length === 0 || !lessonFormData.subjectIds?.length || !lessonFormData.title}
            >
              {isEditingLesson ? 'Update Lesson Plan' : 'Create Lesson Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Create/Edit Dialog */}
      <Dialog open={isAssignmentDialogOpen} onOpenChange={(open) => {
        if (!open) {
          resetAssignmentForm();
        }
        setIsAssignmentDialogOpen(open);
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">
              {editingAssignmentId ? 'Edit Assignment' : 'Create Assignments'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6">
            {/* Student Selection - show when creating OR editing */}
            <div className="space-y-3 sm:space-y-4 pb-4 border-b border-slate-200">
              <div>
                {editingAssignmentId ? (
                  <>
                    <Label className="text-sm font-semibold mb-2 block">Assign to Additional Students</Label>
                    <p className="text-xs text-slate-600 mb-2">Select students in this lesson plan who don't have this assignment yet</p>
                  </>
                ) : (
                  <>
                    <Label className="text-sm font-semibold mb-2 block">Select Students (Optional)</Label>
                    <p className="text-xs text-slate-600 mb-2">Leave unchecked to assign only to this lesson plan's student(s)</p>
                  </>
                )}
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {(() => {
                    // If editing, find which students already have this assignment
                    let availableChildren = children;
                    
                    if (editingAssignmentId) {
                      // Find current assignment and its plan
                      let currentAssignment = null;
                      let currentPlan = null;
                      for (const plan of plans) {
                        if (plan.assignments) {
                          const found = plan.assignments.find(a => a.id === editingAssignmentId);
                          if (found) {
                            currentAssignment = found;
                            currentPlan = plan;
                            break;
                          }
                        }
                      }
                      
                      if (currentAssignment && currentPlan) {
                        // Find all children who already have this assignment in matching lesson plans
                        const childrenWithAssignment = new Set();
                        for (const plan of plans) {
                          if (plan.assignments &&
                              plan.title === currentPlan.title &&
                              plan.subject_id === currentPlan.subject_id &&
                              plan.start_date === currentPlan.start_date &&
                              plan.end_date === currentPlan.end_date) {
                            const hasAssignment = plan.assignments.some(a => 
                              a.title === currentAssignment.title &&
                              a.due_date === currentAssignment.due_date
                            );
                            if (hasAssignment) {
                              childrenWithAssignment.add(plan.child_id);
                            }
                          }
                        }
                        
                        // Filter to show only children in this lesson plan who don't have the assignment
                        const childrenInLessonPlan = plans
                          .filter(p => 
                            p.title === currentPlan.title &&
                            p.subject_id === currentPlan.subject_id &&
                            p.start_date === currentPlan.start_date &&
                            p.end_date === currentPlan.end_date
                          )
                          .map(p => p.child_id);
                        
                        availableChildren = children.filter(child => 
                          childrenInLessonPlan.includes(child.id) &&
                          !childrenWithAssignment.has(child.id)
                        );
                      }
                    }
                    
                    if (editingAssignmentId && availableChildren.length === 0) {
                      return <p className="text-sm text-slate-500">✓ All students in this lesson plan have this assignment. Saving will update it for everyone.</p>;
                    }
                    
                    if (editingAssignmentId && availableChildren.length > 0) {
                      return (
                        <>
                          <p className="text-sm text-slate-500 mb-2">Optional: Add this assignment to additional students:</p>
                          {availableChildren.map(child => (
                            <div key={child.id} className="flex items-center">
                              <Checkbox
                                id={`assign-${child.id}`}
                                checked={assignmentStudents.includes(child.id)}
                                onCheckedChange={() => toggleAssignmentStudent(child.id)}
                              />
                              <label htmlFor={`assign-${child.id}`} className="ml-2 text-xs sm:text-sm cursor-pointer">
                                {child.name}
                              </label>
                            </div>
                          ))}
                        </>
                      );
                    }
                    
                    return availableChildren.map(child => (
                      <div key={child.id} className="flex items-center">
                        <Checkbox
                          id={`assign-${child.id}`}
                          checked={assignmentStudents.includes(child.id)}
                          onCheckedChange={() => toggleAssignmentStudent(child.id)}
                        />
                        <label htmlFor={`assign-${child.id}`} className="ml-2 text-xs sm:text-sm cursor-pointer">
                          {child.name}
                        </label>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {!editingAssignmentId && (
                <div>
                  <Label className="text-sm font-semibold">Subject (Optional)</Label>
                  <Select value={assignmentSubject || undefined} onValueChange={setAssignmentSubject}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Use lesson plan's subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(subject => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.icon} {subject.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Cascade Dates Option (only when editing) */}
            {editingAssignmentId && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cascade-dates"
                    checked={cascadeDates}
                    onChange={(e) => setCascadeDates(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="cascade-dates" className="text-sm font-medium cursor-pointer">
                    Cascade date changes to subsequent assignments
                  </label>
                </div>
                <p className="text-xs text-slate-500 ml-6 mt-1">
                  When enabled, changing the due date will automatically adjust all later assignments in this lesson plan while maintaining day gaps. Submitted assignments will be skipped.
                </p>
              </div>
            )}

            {/* Assignment Rows */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Assignments</Label>

              {/* Assignment Rows */}
              {assignmentRows.map((row, index) => (
                <div key={row.id} className="flex flex-col gap-3 p-3 sm:p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                  {editingAssignmentId ? (
                    /* Edit Mode Layout - Stacked with Rich Text Editor */
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Title *</Label>
                          <Input
                            value={row.title}
                            onChange={(e) => updateAssignmentRow(row.id, 'title', e.target.value)}
                            placeholder="Assignment title"
                            className="text-sm"
                          />
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Type *</Label>
                          <Select 
                            value={row.assignmentType} 
                            onValueChange={(value) => updateAssignmentRow(row.id, 'assignmentType', value)}
                          >
                            <SelectTrigger className="text-sm h-10">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {assignmentTypes.map(type => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Points *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={row.pointsPossible}
                            onChange={(e) => updateAssignmentRow(row.id, 'pointsPossible', e.target.value)}
                            placeholder="100"
                            className="text-sm"
                          />
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Due Date *</Label>
                          <Input
                            type="date"
                            value={row.dueDate}
                            onChange={(e) => updateAssignmentRow(row.id, 'dueDate', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div className="w-full">
                        <Label className="text-xs mb-1 block">Description</Label>
                        <RichTextEditor
                          value={row.description}
                          onChange={(value) => updateAssignmentRow(row.id, 'description', value)}
                          placeholder="Enter assignment description with formatting..."
                        />
                      </div>

                      <div className="w-full">
                        <Label className="text-xs mb-1 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5" /> Standards Covered</Label>
                        <StandardsMultiSelect
                          standards={standards}
                          value={row.standardIds || []}
                          onChange={(ids) => updateAssignmentRow(row.id, 'standardIds', ids)}
                          testId={`assignment-standards-${row.id}`}
                        />
                      </div>
                    </>
                  ) : (
                    /* Create Mode Layout - With Rich Text Editor */
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Title *</Label>
                          <Input
                            value={row.title}
                            onChange={(e) => updateAssignmentRow(row.id, 'title', e.target.value)}
                            placeholder="Assignment title"
                            className="text-sm"
                          />
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Type *</Label>
                          <Select 
                            value={row.assignmentType} 
                            onValueChange={(value) => updateAssignmentRow(row.id, 'assignmentType', value)}
                          >
                            <SelectTrigger className="text-sm h-10">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {assignmentTypes.map(type => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Points *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={row.pointsPossible}
                            onChange={(e) => updateAssignmentRow(row.id, 'pointsPossible', e.target.value)}
                            placeholder="100"
                            className="text-sm"
                          />
                        </div>
                        
                        <div className="w-full">
                          <Label className="text-xs mb-1 block">Due Date *</Label>
                          <Input
                            type="date"
                            value={row.dueDate}
                            onChange={(e) => updateAssignmentRow(row.id, 'dueDate', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div className="w-full">
                        <Label className="text-xs mb-1 block">Description</Label>
                        <RichTextEditor
                          value={row.description}
                          onChange={(value) => updateAssignmentRow(row.id, 'description', value)}
                          placeholder="Enter assignment description with formatting..."
                        />
                      </div>

                      <div className="w-full">
                        <Label className="text-xs mb-1 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5" /> Standards Covered</Label>
                        <StandardsMultiSelect
                          standards={standards}
                          value={row.standardIds || []}
                          onChange={(ids) => updateAssignmentRow(row.id, 'standardIds', ids)}
                          testId={`assignment-standards-${row.id}`}
                        />
                      </div>
                      
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-slate-500">Assignment #{index + 1}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => duplicateAssignmentRow(row.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2"
                          title="Duplicate this assignment"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {assignmentRows.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeAssignmentRow(row.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            title="Remove this assignment"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Add Another Button */}
              {!editingAssignmentId && assignmentRows.length < 31 && (
                <Button
                  variant="outline"
                  onClick={addAssignmentRow}
                  className="w-full border-dashed border-2 text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Assignment ({assignmentRows.length}/31)
                </Button>
              )}
            </div>

            {/* Assignment Visibility Rules (only when editing single assignment) */}
            {editingAssignmentId && (
              <div className="border-t pt-4 space-y-3">
                <Label className="text-sm font-semibold">Student Visibility</Label>
                <p className="text-xs text-slate-600">Control when students can see this assignment</p>
                
                <Select
                  value={assignmentRows[0]?.visibilityRule || 'always'}
                  onValueChange={(value) => updateAssignmentRow(assignmentRows[0].id, 'visibilityRule', value)}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Always visible (default)</SelectItem>
                    <SelectItem value="on_date">Show on a specific date</SelectItem>
                    <SelectItem value="after_previous">Show after completing another assignment</SelectItem>
                  </SelectContent>
                </Select>
                
                {assignmentRows[0]?.visibilityRule === 'on_date' && (
                  <div className="mt-2">
                    <Label className="text-xs text-slate-600">Visible starting:</Label>
                    <Input
                      type="date"
                      value={assignmentRows[0]?.visibilityDate ? assignmentRows[0].visibilityDate.split('T')[0] : ''}
                      onChange={(e) => updateAssignmentRow(assignmentRows[0].id, 'visibilityDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                      className="mt-1"
                    />
                  </div>
                )}
                
                {assignmentRows[0]?.visibilityRule === 'after_previous' && (
                  <div className="mt-2">
                    <Label className="text-xs text-slate-600">Show after completing:</Label>
                    <Select
                      value={assignmentRows[0]?.visibilityAfterAssignmentId || ''}
                      onValueChange={(value) => updateAssignmentRow(assignmentRows[0].id, 'visibilityAfterAssignmentId', value)}
                    >
                      <SelectTrigger className="bg-white mt-1">
                        <SelectValue placeholder="Select assignment..." />
                      </SelectTrigger>
                      <SelectContent>
                        {plans
                          .flatMap(p => p.assignments || [])
                          .filter(a => a.id !== editingAssignmentId)
                          .slice(0, 50)
                          .map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Move to Different Lesson Plan (only when editing) */}
            {editingAssignmentId && availablePlansForMove.length > 1 && (
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold">Move to Different Lesson Plan</Label>
                <p className="text-xs text-slate-600 mb-2">
                  Move this assignment (and all shared copies) to a different lesson plan in the same subject
                </p>
                <Select 
                  value={selectedPlanIdForMove} 
                  onValueChange={async (newPlanId) => {
                    if (newPlanId !== selectedPlanIdForMove) {
                      if (window.confirm('This will move ALL copies of this assignment (across all children) to the selected lesson plan. Continue?')) {
                        try {
                          await api.moveAssignmentToPlan(editingAssignmentId, newPlanId);
                          toast({
                            title: 'Assignment Moved',
                            description: 'Assignment and all shared copies have been moved to the new lesson plan.'
                          });
                          setIsAssignmentDialogOpen(false);
                          await loadData();
                        } catch (error) {
                          toast({
                            title: 'Error',
                            description: error.message || 'Failed to move assignment',
                            variant: 'destructive'
                          });
                        }
                      } else {
                        setSelectedPlanIdForMove(selectedPlanIdForMove); // Revert
                      }
                    }
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlansForMove.map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Assignment Attachments */}
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold">📎 Attachments (PDFs, worksheets, etc.)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  id="assignment-file-upload"
                  className="hidden"
                  onChange={(e) => handleAssignmentFileUpload(e, editingAssignmentId)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('assignment-file-upload').click()}
                  disabled={uploadingFile}
                  className="w-full"
                >
                  {uploadingFile ? 'Uploading...' : '📎 Add File'}
                </Button>
              </div>
              
              {assignmentAttachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {assignmentAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                      <span className="text-sm truncate flex-1">{att.name}</span>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(att.url, '_blank')}
                          className="text-blue-600"
                        >
                          📥
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Always just remove from local state
                            // Will be saved when clicking "Update Assignment"
                            setAssignmentAttachments(assignmentAttachments.filter((_, i) => i !== idx));
                          }}
                          className="text-red-600"
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Assignment Checklist - Progress Tracker & Resources */}
            <div className="border-t pt-4 space-y-4">
              {[
                { key: 'progress', label: '✓ Progress Tracker', help: 'Checkable steps students complete as they work. Drag to reorder.', addLabel: 'Step', placeholder: 'Enter a step (e.g., Complete page 5)' },
                { key: 'resources', label: '📚 Resources', help: 'Materials or links students check off as they use them. Drag to reorder.', addLabel: 'Resource', placeholder: 'Enter a resource (e.g., Watch https://youtube.com/...)' },
              ].map(section => {
                const allItems = assignmentRows[0]?.checklist || [];
                const sectionItems = allItems.map((it, i) => ({ it, i })).filter(({ it }) => (it.section || 'progress') === section.key);
                const sectionItemIds = sectionItems.map(({ it }) => it.id);

                const handleDragEnd = (event) => {
                  const { active, over } = event;
                  if (active.id !== over?.id) {
                    const oldIndex = sectionItems.findIndex(({ it }) => it.id === active.id);
                    const newIndex = sectionItems.findIndex(({ it }) => it.id === over.id);
                    
                    // Create new array with items reordered within this section
                    const reorderedSectionItems = arrayMove(sectionItems, oldIndex, newIndex);
                    
                    // Rebuild full checklist: other section items stay in place, this section gets reordered
                    const otherSectionItems = allItems.filter(it => (it.section || 'progress') !== section.key);
                    const newChecklist = [...otherSectionItems, ...reorderedSectionItems.map(({ it }) => it)];
                    
                    updateAssignmentRow(assignmentRows[0].id, 'checklist', newChecklist);
                  }
                };

                return (
                  <div key={section.key} data-testid={`assignment-${section.key}-editor`}>
                    <Label className="text-sm font-semibold">{section.label}</Label>
                    <p className="text-xs text-slate-600 mb-2">{section.help}</p>
                    <div className="space-y-2">
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={sectionItemIds} strategy={verticalListSortingStrategy}>
                          {sectionItems.map(({ it, i }) => (
                            <SortableChecklistItem
                              key={it.id}
                              item={it}
                              index={i}
                              allItems={allItems}
                              section={section.key}
                              placeholder={section.placeholder}
                              onUpdate={(nc) => updateAssignmentRow(assignmentRows[0].id, 'checklist', nc)}
                              onDelete={(nc) => updateAssignmentRow(assignmentRows[0].id, 'checklist', nc)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                      {/* Bulk Add Input - supports pasting multiple lines */}
                      <div className="mt-2">
                        <Textarea
                          placeholder={`Paste or type multiple items (one per line)...\n${section.placeholder}`}
                          className="text-sm min-h-[60px] resize-none"
                          data-testid={`bulk-add-${section.key}-textarea`}
                          onKeyDown={(e) => {
                            // On Enter without Shift, add items
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              const lines = e.target.value.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                              if (lines.length > 0) {
                                const newItems = lines.map((text, idx) => ({
                                  id: Date.now() + idx,
                                  text,
                                  completed: false,
                                  section: section.key
                                }));
                                const nc = [...allItems, ...newItems];
                                updateAssignmentRow(assignmentRows[0].id, 'checklist', nc);
                                e.target.value = '';
                              }
                            }
                          }}
                          onPaste={(e) => {
                            // Auto-add items when pasting multi-line content
                            setTimeout(() => {
                              const val = e.target.value;
                              const lines = val.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                              if (lines.length > 1) {
                                const newItems = lines.map((text, idx) => ({
                                  id: Date.now() + idx,
                                  text,
                                  completed: false,
                                  section: section.key
                                }));
                                const nc = [...allItems, ...newItems];
                                updateAssignmentRow(assignmentRows[0].id, 'checklist', nc);
                                e.target.value = '';
                              }
                            }, 0);
                          }}
                        />
                        <p className="text-xs text-slate-500 mt-1">Press Enter to add items. Paste a list to add all at once.</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-2 pt-4 border-t border-slate-200">
              <Button
                onClick={editingAssignmentId ? handleUpdateAssignment : handleAddAssignment}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-6 text-base font-semibold"
              >
                {editingAssignmentId ? 'Update Assignment' : `Create ${assignmentRows.length} Assignment${assignmentRows.length > 1 ? 's' : ''}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  resetAssignmentForm();
                  setIsAssignmentDialogOpen(false);
                }}
                className="px-8 py-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cascade Dates Confirmation Dialog */}
      <Dialog open={cascadeConfirmOpen} onOpenChange={setCascadeConfirmOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Cascading Date Changes</DialogTitle>
            <DialogDescription>
              The following assignments will have their due dates adjusted to maintain the day gaps:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Assignment</th>
                  <th className="text-left p-2">Old Date</th>
                  <th className="text-left p-2">New Date</th>
                </tr>
              </thead>
              <tbody>
                {cascadePreview.map((update, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">{update.assignment_title || 'Current Assignment'}</td>
                    <td className="p-2">{new Date(update.old_date).toLocaleDateString()}</td>
                    <td className="p-2 font-semibold text-blue-600">
                      {new Date(update.new_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCascadeConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmCascade} className="bg-blue-600 hover:bg-blue-700">
              Confirm Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Lesson Plan Dialog */}
      <Dialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Lesson Plan</DialogTitle>
            <DialogDescription>
              Select which children should receive a copy of this lesson plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              {children.map(child => (
                <div key={child.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`duplicate-${child.id}`}
                    checked={duplicateSelectedChildren.includes(child.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDuplicateSelectedChildren([...duplicateSelectedChildren, child.id]);
                      } else {
                        setDuplicateSelectedChildren(duplicateSelectedChildren.filter(id => id !== child.id));
                      }
                    }}
                    className="h-4 w-4"
                  />
                  <label htmlFor={`duplicate-${child.id}`} className="text-sm cursor-pointer">
                    {child.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDuplicatePlan}>
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default LessonPlansPage;
